import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Tent, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

interface EditorSidebarProps {
  isOpen: boolean;
  basePath?: string;
}

interface SidebarLinkProps {
  to: string;
  icon: React.ElementType;
  label: string;
  isActive: boolean;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ 
  to, 
  icon: Icon, 
  label, 
  isActive
}) => {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm",
        isActive 
          ? "bg-primary-100 text-primary-700 border border-primary-200" 
          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
      )}
    >
      <Icon size={18} />
      <span>{label}</span>
    </Link>
  );
};

const EditorSidebar: React.FC<EditorSidebarProps> = ({ 
  isOpen, 
  basePath = "/editor" 
}) => {
  const location = useLocation();
  
  const isActive = (path: string) => location.pathname === path;
  
  const navigationItems = [
    {
      path: `${basePath}`,
      label: "Dashboard",
      icon: LayoutDashboard,
    },
    {
      path: `${basePath}/camps`,
      label: "Camp Management",
      icon: Tent,
    },
    {
      path: `${basePath}/ncaa-calendar`,
      label: "NCAA Calendar",
      icon: Calendar,
    },
  ];

  if (!isOpen) {
    return (
      <div className="w-16 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col overflow-y-auto transition-all duration-300">
        <div className="flex flex-col items-center py-4 h-full space-y-2">
          {navigationItems.map((item) => {
            const ItemIcon = item.icon;
            const isItemActive = isActive(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "p-3 rounded-lg transition-colors",
                  isItemActive 
                    ? "bg-primary-100 text-primary-700" 
                    : "text-gray-600 hover:bg-gray-100"
                )}
              >
                <ItemIcon size={20} />
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col overflow-y-auto transition-all duration-300">
      <div className="p-4 flex flex-col h-full">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-primary-700">Editor Dashboard</h2>
          <p className="text-sm text-gray-600">Camp & Calendar Management</p>
        </div>

        <div className="space-y-1.5">
          {navigationItems.map((item) => (
            <SidebarLink
              key={item.path}
              to={item.path}
              icon={item.icon}
              label={item.label}
              isActive={isActive(item.path)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EditorSidebar;